import { apiGet, apiPost, apiPut, apiRoutes, buildApiRoute } from "./entityApiShared.js";
import { requireCmtsIdentifier } from "../core/payloads/cmtsTypedPayloads.js";
import { requireCmtsGuid } from "../core/utils/cmtsGuid.js";
import {
  parseCapaRecord,
  parseCapaRecordList,
  type CapaActionRequest,
  type CapaCreateRequest,
  type CapaDto,
  type CapaUpdateRequest,
} from "../../types/capa.types.js";
import { parseEligibleLifecycleUsers } from "./lifecycleEligibleUsers.js";
import type { DropdownOption } from "../types/api.types.js";

export type { CapaActionRequest, CapaCreateRequest, CapaDto, CapaRecordDto, CapaUpdateRequest } from "../../types/capa.types.js";

type BackendCapaCreateRequest = Readonly<Omit<CapaCreateRequest, "complaintId">>;
type BackendCapaUpdateRequest = Readonly<Omit<CapaUpdateRequest, "rowVersion"> & { expectedRowVersion?: string | undefined }>;
type BackendCapaActionRequest = Readonly<{
  remarks?: string | undefined;
  reason?: string | undefined;
  assignedToUserId?: string | undefined;
  expectedRowVersion?: string | undefined;
}>;
type BackendCapaCompleteRequest = Readonly<{
  closedStatusId: string;
  effectivenessStatusId?: string | undefined;
  closureComment?: string | undefined;
  expectedRowVersion?: string | undefined;
}>;

function optionalText(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function createRequest(payload: CapaCreateRequest): BackendCapaCreateRequest {
  return {
    actionTypeId: requireCmtsGuid(payload.actionTypeId, "CAPA create actionTypeId"),
    actionDescription: payload.actionDescription.trim(),
    ownerUserId: requireCmtsGuid(payload.ownerUserId, "CAPA create ownerUserId"),
    dueDate: payload.dueDate,
    statusId: requireCmtsGuid(payload.statusId, "CAPA create statusId"),
    effectivenessRequired: payload.effectivenessRequired,
    ...(payload.effectivenessCheckDate ? { effectivenessCheckDate: payload.effectivenessCheckDate } : {}),
  };
}

function updateRequest(payload: CapaUpdateRequest): BackendCapaUpdateRequest {
  const { rowVersion, ...business } = payload;
  return {
    ...business,
    ...(payload.actionTypeId ? { actionTypeId: requireCmtsGuid(payload.actionTypeId, "CAPA update actionTypeId") } : {}),
    ...(payload.ownerUserId ? { ownerUserId: requireCmtsGuid(payload.ownerUserId, "CAPA update ownerUserId") } : {}),
    ...(rowVersion ? { expectedRowVersion: rowVersion } : {}),
  };
}

function actionRequest(payload: CapaActionRequest): BackendCapaActionRequest {
  return {
    ...(optionalText(payload.remarks) ? { remarks: payload.remarks!.trim() } : {}),
    ...(optionalText(payload.reason) ? { reason: payload.reason!.trim() } : {}),
    ...(payload.assignedToUserId ? { assignedToUserId: requireCmtsGuid(payload.assignedToUserId, "CAPA action assignedToUserId") } : {}),
    ...(payload.rowVersion ? { expectedRowVersion: payload.rowVersion } : {}),
  };
}

function completeRequest(payload: CapaActionRequest): BackendCapaCompleteRequest {
  return {
    closedStatusId: requireCmtsGuid(payload.closedStatusId, "CAPA complete closedStatusId"),
    ...(payload.effectivenessStatusId ? { effectivenessStatusId: requireCmtsGuid(payload.effectivenessStatusId, "CAPA complete effectivenessStatusId") } : {}),
    ...(optionalText(payload.closureComment) ? { closureComment: payload.closureComment!.trim() } : {}),
    ...(payload.rowVersion ? { expectedRowVersion: payload.rowVersion } : {}),
  };
}

export const capaApi = {
  queue: async (signal?: AbortSignal): Promise<readonly CapaDto[]> => parseCapaRecordList(await apiGet<unknown>(buildApiRoute(apiRoutes.capa.queue), { signal })),
  permittedOwners: async (complaintId: string, signal?: AbortSignal): Promise<readonly DropdownOption[]> =>
    parseEligibleLifecycleUsers(await apiGet<unknown>(
      buildApiRoute(apiRoutes.capa.permittedOwners, {
        complaintId: requireCmtsIdentifier(complaintId, "complaintId"),
      }),
      { signal },
    )),
  list: async (complaintId: string, signal?: AbortSignal): Promise<readonly CapaDto[]> => parseCapaRecordList(await apiGet<unknown>(buildApiRoute(apiRoutes.capa.list, { complaintId: requireCmtsIdentifier(complaintId, "complaintId") }), { signal })),
  get: async (complaintId: string, id: string, signal?: AbortSignal): Promise<CapaDto> => parseCapaRecord(await apiGet<unknown>(buildApiRoute(apiRoutes.capa.get, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "capaId") }), { signal })),
  create: async (complaintId: string, payload: CapaCreateRequest, idempotencyKey?: string): Promise<CapaDto> => parseCapaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.capa.create, { complaintId: requireCmtsIdentifier(complaintId, "complaintId") }), createRequest(payload), { idempotencyKey })),
  update: async (complaintId: string, id: string, payload: CapaUpdateRequest, idempotencyKey?: string): Promise<CapaDto> => parseCapaRecord(await apiPut<unknown>(buildApiRoute(apiRoutes.capa.update, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "capaId") }), updateRequest(payload), { idempotencyKey })),
  submit: async (complaintId: string, id: string, payload: CapaActionRequest, idempotencyKey?: string): Promise<CapaDto> => parseCapaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.capa.submit, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "capaId") }), actionRequest(payload), { idempotencyKey })),
  assign: async (complaintId: string, id: string, payload: CapaActionRequest, idempotencyKey?: string): Promise<CapaDto> => parseCapaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.capa.assign, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "capaId") }), actionRequest(payload), { idempotencyKey })),
  requestVerification: async (complaintId: string, id: string, payload: CapaActionRequest, idempotencyKey?: string): Promise<CapaDto> => parseCapaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.capa.requestVerification, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "capaId") }), actionRequest(payload), { idempotencyKey })),
  reopen: async (complaintId: string, id: string, payload: CapaActionRequest, idempotencyKey?: string): Promise<CapaDto> => parseCapaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.capa.reopen, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "capaId") }), actionRequest(payload), { idempotencyKey })),
  complete: async (complaintId: string, id: string, payload: CapaActionRequest, idempotencyKey?: string): Promise<CapaDto> => parseCapaRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.capa.complete, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "capaId") }), completeRequest(payload), { idempotencyKey })),
};
